import React, { useState, useEffect } from 'react';
import { X, BookOpen, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { API_URL as API } from '@/utils/apiConfig';

import { useTranslation } from 'react-i18next';

/**
 * ScripturePopup — universal popup for scripture references.
 *
 * Used across DailyReflectionPage, DidYouKnowPage, and anywhere else
 * we want to show a verse inline (with a "Read Full Chapter" CTA) so
 * tapping a reference doesn't yank the user away from their current
 * reading context.
 *
 * Props:
 *   reference        — string, e.g. "John 3:16" or "1 Corinthians 13:4-7"
 *   onClose          — () => void; fires when overlay / X / Esc tapped
 *   onReadChapter?   — optional override. Defaults to navigating to
 *                       /bible with state.targetReference so the
 *                       reader opens at the right book/chapter/verse.
 */
export default function ScripturePopup({ reference, onClose, onReadChapter }) {
  const {
    t
  } = useTranslation();

  const navigate = useNavigate();
  const [verseText, setVerseText] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Get user's preferred translation, default to ESV.
        let translation = 'esv';
        try {
          const settingsRes = await axios.get(`${API}/settings`, { withCredentials: true });
          translation = settingsRes.data?.primary_translation || 'esv';
        } catch {
          // Settings fetch failure is non-fatal; we just use the ESV default.
        }
        const res = await axios.post(
          `${API}/bible/passage`,
          { reference, translation },
          { withCredentials: true }
        );
        const verses = res.data?.verses || [];
        const text = verses
          .map((v) => `${v.text?.replace(/<[^>]+>/g, '') || ''}`)
          .join(' ');
        if (!cancelled) setVerseText(text || 'Verse text not available.');
      } catch (err) {
        console.error('Verse popup fetch error:', err);
        if (!cancelled) setVerseText('Unable to load verse.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reference]);

  // Esc-to-close — small ergonomic win for desktop power users.
  useEffect(() => {
    const handler = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const handleReadChapter = () => {
    if (onReadChapter) {
      onReadChapter(reference);
      return;
    }
    onClose();
    // Parse "John 3:16" / "1 Corinthians 13:4-7" → { book, chapter, verse }
    const match = reference.match(/^(.+?)\s+(\d+)(?::(\d+)(?:\s*[-–]\s*\d+)?)?$/);
    if (match) {
      navigate('/bible', {
        state: {
          targetReference: {
            book: match[1],
            chapter: match[2],
            verse: match[3] || '',
          },
        },
      });
    } else {
      navigate('/bible');
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-5"
      onClick={onClose}
      data-testid="scripture-popup-overlay"
    >
      <div
        className="absolute inset-0 backdrop-blur-md"
        style={{ background: 'rgba(0,0,0,0.6)' }}
      />
      <div
        className="relative w-full max-w-md rounded-2xl p-6 space-y-4 shadow-2xl"
        style={{
          background: 'var(--theme-surface)',
          border: '1px solid var(--theme-border)',
        }}
        onClick={(e) => e.stopPropagation()}
        data-testid="scripture-popup-card"
      >
        <div className="flex items-center justify-between">
          <h3
            className="text-lg font-semibold"
            style={{ color: 'var(--theme-accent)' }}
          >
            {reference}
          </h3>
          <button
            onClick={onClose}
            className="p-1 rounded-full hover:bg-[var(--theme-hover)]"
            data-testid="scripture-popup-close"
          >
            <X className="h-4 w-4" style={{ color: 'var(--theme-textSecondary)' }} />
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2
              className="h-5 w-5 animate-spin"
              style={{ color: 'var(--theme-accent)' }}
            />
          </div>
        ) : (
          <p
            className="text-sm leading-relaxed overflow-y-auto"
            style={{
              color: 'var(--theme-text)',
              maxHeight: 'min(40vh, calc(var(--visual-vh, 100vh) - 240px))',
            }}
          >
            {verseText}
          </p>
        )}

        <Button
          onClick={handleReadChapter}
          className="w-full rounded-xl py-3 text-sm font-medium"
          style={{ background: 'var(--theme-accent)', color: '#fff' }}
          data-testid="read-full-chapter-popup-btn"
        >
          <BookOpen className="h-4 w-4 mr-2" />
          {t('generated.scripturepopup_read_full_chapter_621d')}
        </Button>
      </div>
    </div>
  );
}
